import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// third party
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { useIntl } from 'react-intl';

// project imports
import AuthWrapper1 from './AuthWrapper1';
import WelcomeBanner from './WelcomeBanner';
import SignupLogo from 'ui-component/SignupLogo';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { resendOTP, submitOTP } from '../../../utils/CognitoCustomChallengeService';

// Create userPool instance
const userPool = new CognitoUserPool({
  UserPoolId: process.env.awsPoolId,
  ClientId: process.env.clientId
});

// Simple CognitoUserStore implementation
const CognitoUserStore = {
  _user: null,
  set(user) {
    this._user = user;
  },
  get() {
    return this._user;
  },
  clear() {
    this._user = null;
  },
  ensure(username) {
    if (this._user) return this._user;
    const savedSession = sessionStorage.getItem('mfa_session');
    if (savedSession && username) {
      const rebuiltUser = new CognitoUser({ Username: username, Pool: userPool });
      try {
        rebuiltUser.Session = savedSession;
      } catch {}
      this.set(rebuiltUser);
      return rebuiltUser;
    }
    return null;
  }
};

// ===========================|| TWO FACTOR AUTH VERIFICATION ||=========================== //

export default function TwoFactorAuthVerification() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const inputRefs = useRef([]);

  const username = localStorage.getItem('username') || sessionStorage.getItem('mfa_username');
  const challengeUsername = username;
  const method = sessionStorage.getItem('mfa_method') || 'email';
  const delivery = sessionStorage.getItem('mfa_delivery') || '';
  
  // Mask contact info
  const maskEmail = (e) => (e || '').replace(/(^.).*(@.).*$/, (_, a1, a2) => `${a1}***${a2}***`);
  const maskPhone = (p) => (p || '').replace(/^\+?\d{2}(\d+)(\d{2})$/, (_m, mid, last2) => `+**${'*'.repeat(Math.max(0, mid.length))}${last2}`);
  const maskedContact = method === 'sms' ? maskPhone(delivery) : maskEmail(delivery);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleResend = async () => {
    try {
      setErrorMsg('');
      setIsResending(true);

      let cognitoUser = CognitoUserStore.ensure(challengeUsername);
      if (!cognitoUser) {
        const savedSession = sessionStorage.getItem('mfa_session');
        if (savedSession && challengeUsername) {
          const rebuiltUser = new CognitoUser({ Username: challengeUsername, Pool: userPool });
          try {
            rebuiltUser.Session = savedSession;
          } catch {}
          CognitoUserStore.set(rebuiltUser);
        }
      }

      const userForResend = CognitoUserStore.ensure(challengeUsername);
      if (!userForResend || !userForResend.Session) {
        setErrorMsg('Your verification session expired. Please log in again.');
        setIsResending(false);
        return;
      }

      const challengeParameters = {
        factorName: method,
        factorValue: delivery
      };

        await resendOTP(userForResend, challengeParameters);

      dispatch(
        openSnackbar({
          open: true,
          message: 'Verification code resent successfully',
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: false
        })
      );
    } catch (e) {
      const message = e?.message || 'Failed to resend code';
      setErrorMsg(message);
      dispatch(
        openSnackbar({
          open: true,
          message: message,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
    } finally {
      setIsResending(false);
    }
  };

  const buildAuthClientMetadata = () => {
    const cookies = document.cookie.split(';');
    let trustId = '';
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name.startsWith('UUID_')) trustId = value;
    }
    const clientMetadata = {
      trustID: trustId || localStorage.getItem('trust_id') || '',
      sourceEvent: 'insight-web',
      language: 'en',
      appType: 'web',
    };
    return clientMetadata;
  };

  /**
   * Verifies the 6-digit code with Cognito and routes to the next step.
   */
  const verifyAndProceed = async (enteredCode) => {
    // Ensure we have the SAME CognitoUser instance that received the challenge
    let cognitoUserForChallenge = CognitoUserStore.ensure(challengeUsername);

    // Fallback if the store was not used or page reloaded: rebuild and rehydrate the Session
    if (!cognitoUserForChallenge) {
      const challengeSessionToken = sessionStorage.getItem('mfa_session');
      if (!challengeUsername || !challengeSessionToken) {
        throw new Error('Your verification session expired. Please log in again.');
      }
      cognitoUserForChallenge = new CognitoUser({ Username: challengeUsername, Pool: userPool });
      try {
        cognitoUserForChallenge.Session = challengeSessionToken;
      } catch {}
      CognitoUserStore.set(cognitoUserForChallenge);
    }

    // Compose the challenge context carried from the login step
    const challengeContext = {
      factorName: method,
      factorValue: delivery
    };

    // Send the OTP (submitOTP throws on invalid/expired codes)
    const submitResult = await submitOTP(cognitoUserForChallenge, String(enteredCode).trim(), challengeContext);
    if (submitResult?.type !== 'success') {
      throw new Error("That code didn't work. Try again or tap 'Resend Message'.");
    }

    // Retrieve and persist tokens
    await new Promise((resolve, reject) =>
      cognitoUserForChallenge.getSession((err, userSession) => {
        if (err) return reject(err);
        localStorage.setItem('idtoken', userSession.getIdToken().getJwtToken());
        localStorage.setItem('token', userSession.getAccessToken().getJwtToken());
        localStorage.setItem('refreshtoken', userSession.getRefreshToken().getToken());
        localStorage.setItem('id_token', userSession.getIdToken().getJwtToken());
        localStorage.setItem('access_token', userSession.getAccessToken().getJwtToken());
        localStorage.setItem('refresh_token', userSession.getRefreshToken().getToken());
        localStorage.setItem('serviceToken', userSession.getAccessToken().getJwtToken());
        resolve();
      })
    );

    // Clean up challenge state
    try {
      CognitoUserStore.clear();
    } catch {}

    const metadata = buildAuthClientMetadata();

    // Next screen in your flow
    if (metadata.trustID || localStorage.getItem('insight_trusted') || localStorage.getItem('trust_id')) {
      console.log('Trust detected, redirecting to /CamSdk');
      navigate('/');
    } else {
      navigate('/trust-browser');
    }
  };

  const handleVerify = async () => {
    const verificationCodeString = verificationCode.join('');
    if (!/^\d{6}$/.test(verificationCodeString)) {
      setErrorMsg('Please enter the complete 6-digit code');
      return;
    }
    setIsVerifying(true);
    setErrorMsg('');
    try {
      await verifyAndProceed(verificationCodeString);
    } catch (error) {
      const friendlyMessage =
        error?.code === 'CodeMismatchException'
          ? "That code is incorrect. Try again."
          : error?.code === 'ExpiredCodeException'
          ? 'Code expired. Tap "Resend Message."'
          : error?.code === 'NotAuthorizedException'
          ? 'Session expired. Please log in again.'
          : error?.message || 'Verification failed';
      setErrorMsg(friendlyMessage);
      dispatch(
        openSnackbar({
          open: true,
          message: friendlyMessage,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleTryAnotherMethod = () => {
    // Navigate back to login to try different method
    navigate('/login', { replace: true });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);
    setErrorMsg(''); // Clear error when user types

    // Move to next input if value is entered
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Move to previous input if value is deleted
    if (!digit && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setVerificationCode(newCode);
      setErrorMsg('');
      // Focus the last input
      inputRefs.current[5]?.focus();
    }
  };

  const isCodeComplete = verificationCode.every((digit) => digit !== '');

  return (
    <AuthWrapper1>
      <Grid container sx={{ m: { xs: 2, md: 0 }, minHeight: 'calc(100vh - 0px)' }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Grid size={12} sx={{ my: { xs: 1, md: 4 }, ml: { xs: 0, md: 6 }, textAlign: { xs: 'center', md: 'left' } }}>
            <SignupLogo /> 
          </Grid>
          <Grid container sx={{ height: 'calc(100% - 250px)' }}>
            <Grid
              container
              spacing={0.5}
              sx={{
                maxWidth: '360px',
                m: '0 auto 0 auto',
                alignItems: 'center',
                justifyContent: 'center',
                '@media (min-width: 980px)': {
                  position: 'relative',
                  right: '-50px',
                  zIndex: 3
                },
                '@media (min-width: 900px) and (max-width: 980px)': {
                  maxWidth: '300px'
                }
              }}
            >
              <Stack spacing={3.7} sx={{ width: '100%' }}>
                {/* Back arrow and title */}
                 <Box sx={{ display: 'flex', alignItems: 'center',width: '100%', justifyContent: 'center', position: 'relative' }}>
                 <IconButton onClick={handleBack} sx={{ mr: 1, color: 'grey.900', position:'absolute', top:{xs:-4, md:4},  left:{xs:'0px', md:'-12%'} }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant={'h2'} fontSize={{ xs: '20px', md: '34px' }} sx={{ color: '#000', textAlign: 'center',}}>
                    Account Log in
                  </Typography>
                </Box>

                {/* Instructions */}
                <Box sx={{ textAlign: 'center', mb: 3.7 }}>
                  <Typography variant="h2" sx={{mb: 3, color:'#78909C' }}>
                    Verify your identity
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#78909C', }}>
                    Enter the verification code that was sent to{' '}
                    <Typography component="span" sx={{ fontWeight: 400, color: 'grey.900' }}>
                      {maskedContact}
                    </Typography>
                  </Typography>
                </Box>

                {/* Verification code input */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.3, my: 3 }}>
                  {verificationCode.map((digit, index) => (
                    <OutlinedInput
                      key={index}
                      inputRef={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputProps={{
                        maxLength: 1,
                        style: { textAlign: 'center', fontSize: '18px', padding: '8px' }
                      }}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      sx={{
                        width: '44px',
                        height: '44px',
                        '& .MuiOutlinedInput-input': {
                          textAlign: 'center'
                        }
                      }}
                    />
                  ))}
                </Box>

                {/* Error message */}
                {errorMsg && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="error" sx={{ fontSize: '14px' }}>
                      {errorMsg}
                    </Typography>
                  </Box>
                )}

                {/* Help text */}
                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <Typography variant="body1" sx={{ color: '#78909C', mb: .5 }}>
                    Having trouble receiving your code?
                  </Typography>
                   <Typography variant="body1" sx={{ color: '#78909C', }}>
                    Get your code via {' '}
                    <Typography
                      component="span"
                      onClick={handleTryAnotherMethod}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        color: theme.palette.primary.main
                      }}
                    >
                      {method === 'sms' ? 'email' : 'SMS'}
                    </Typography>
                  </Typography>
                </Box>

                {/* Action buttons */}
                <Box sx={{ mt: 2 }}>
                  <AnimateButton>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleResend}
                      disabled={isResending || isVerifying}
                      startIcon={isResending ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {isResending ? 'Sending…' : `Resend ${method === 'sms' ? 'SMS' : 'Email'}`}
                    </Button>
                  </AnimateButton>
                </Box>

                <Box sx={{ mt: 3.6 }}>
                  <AnimateButton>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleVerify}
                      disabled={!isCodeComplete || isVerifying || isResending}
                      startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {isVerifying ? 'Verifying...' : 'Continue'}
                    </Button>
                  </AnimateButton>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={7} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
          {/* Right Box */}
          <WelcomeBanner />
        </Grid>
      </Grid>
    </AuthWrapper1>
  );
}
